import { useEffect, useId, useState, type ReactNode } from 'react';
import { useT } from '../i18n/i18n';
import { useLearner } from '../learners/LearnerContext';
import { AREA_LABEL, TARGET_STATUS_LABEL, fetchAnalytics, isoDay, type Analytics, type Point, type TargetSeries, type Week } from './api';
import { addDays, parseDay, today, useDates } from './dates';
import './progress.css';
import DateField from '../components/DateField';
import { Area, Bar, BarChart, CartesianGrid, ComposedChart, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { countTicks, headroom, useChartTheme } from '../components/charts';

type Range = { from: string; to: string };
type Preset = 'weeks' | 'term' | 'year' | 'custom';

export function termOf(day: string): Range {
  const d = parseDay(day);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  if (m >= 9) return { from: `${y}-09-01`, to: `${y}-12-31` };
  if (m < 4 || (m === 4 && d.getDate() < 15)) return { from: `${y}-01-01`, to: `${y}-04-14` };
  return { from: `${y}-04-15`, to: `${y}-07-31` };
}

function presetRange(p: Exclude<Preset, 'custom'>): Range {
  const now = today();
  if (p === 'term') return termOf(now);
  if (p === 'year') {
    const y = parseDay(now).getFullYear();
    return { from: isoDay(new Date(y, 0, 1)), to: isoDay(new Date(y, 11, 31)) };
  }
  return { from: addDays(now, -41), to: now };
}

function ms(day: string): number {
  return parseDay(day).getTime();
}

function useSvgId(): string {
  return `p${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
}

export function useValueText() {
  const t = useT();
  return (v: number, unit: string): string => {
    if (unit === '%') return `${v}%`;
    if (unit === '/5') return `${v}/5`;
    if (unit === 'times') return t('{n} times', { n: v });
    if (unit === 'min') return t('{n} min', { n: v });
    return unit ? `${v} ${unit}` : String(v);
  };
}

export function usePlanAnalytics(learnerId: string | null, from: string, to: string) {
  const t = useT();
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!learnerId || !from || !to || to < from) return;
    let live = true;
    setLoading(true);
    setError(null);
    fetchAnalytics(learnerId, from, to)
      .then((a) => { if (live) setData(a); })
      .catch((e) => { if (live) setError(e instanceof Error && e.message ? e.message : t('The progress could not be loaded just now.')); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [learnerId, from, to]);
  return { data, loading, error };
}

function Numbers({ children }: { children: ReactNode }) {
  const t = useT();
  return (
    <details className="planner-prog-numbers">
      <summary>{t('Show the numbers')}</summary>
      <div className="table-wrap">{children}</div>
    </details>
  );
}

function Swatch({ kind }: { kind: 'planned' | 'done' | 'missed' }) {
  const id = useSvgId();
  return (
    <svg className="planner-prog-swatch" width="18" height="14" viewBox="0 0 18 14" aria-hidden="true" focusable="false">
      {kind === 'missed' && <Hatch id={id} />}
      <rect x="1" y="1" width="16" height="12" className={`planner-prog-bar is-${kind}`} fill={kind === 'missed' ? `url(#${id})` : undefined} />
    </svg>
  );
}

function Hatch({ id }: { id: string }) {
  return (
    <defs>
      <pattern id={id} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="6" height="6" className="planner-prog-hatch-bg" />
        <line x1="0" y1="0" x2="0" y2="6" className="planner-prog-hatch-line" />
      </pattern>
    </defs>
  );
}

export function PracticeSummary({ weeks }: { weeks: Week[] }) {
  const t = useT();
  const d = useDates();
  const hatch = useSvgId();
  const planned = weeks.reduce((n, w) => n + w.planned, 0);
  const done = weeks.reduce((n, w) => n + w.done + w.partly, 0);
  const missed = weeks.reduce((n, w) => n + w.missed, 0);
  const pct = planned > 0 ? Math.round((100 * done) / planned) : 0;

  const max = Math.max(1, ...weeks.map((w) => w.planned));
  const top = headroom(max);
  const data = weeks.map((w) => ({ label: d.short(w.weekStart), planned: w.planned, done: w.done + w.partly, missed: w.missed }));
  const { colors, axis, tooltip, animate, duration } = useChartTheme();

  return (
    <section className="planner-prog-section" aria-labelledby={`${hatch}-h`}>
      <h3 id={`${hatch}-h`}>{t('Practised as planned')}</h3>
      {planned === 0 ? (
        <p className="muted">{t('No sessions were planned in this period.')}</p>
      ) : (
        <p className="planner-prog-lead">{t('{done} of {planned} planned sessions done ({pct}%)', { done, planned, pct })}</p>
      )}
      <p className="field-hint">{t("This helps tell 'not practised' apart from 'not learning'.")}</p>
      <div className="planner-prog-chart">
        {planned > 0 && (
          <div role="img" aria-label={t('Sessions each week: {done} of {planned} done or partly done, {missed} missed.', { done, planned, missed })} style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={data} margin={{ top: 16, right: 8, bottom: 0, left: 0 }} barGap={3} barCategoryGap="22%">
                <defs>
                  <pattern id={hatch} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                    <rect width="6" height="6" fill={colors.surface} />
                    <line x1="0" y1="0" x2="0" y2="6" stroke={colors.inkSoft} strokeWidth={2} />
                  </pattern>
                </defs>
                <CartesianGrid stroke={colors.border} strokeDasharray="3 4" vertical={false} />
                <XAxis dataKey="label" tick={axis} tickLine={false} axisLine={{ stroke: colors.border }} minTickGap={16} />
                <YAxis domain={[0, top]} ticks={countTicks(top, max)} allowDecimals={false} tick={axis} tickLine={false} axisLine={false} width={32} />
                <Tooltip {...tooltip} />
                <Bar dataKey="planned" name={t('Planned')} fill={colors.surface} stroke={colors.inkSoft} strokeWidth={1.5} radius={[6, 6, 0, 0]} maxBarSize={18} isAnimationActive={animate} animationDuration={duration} />
                <Bar dataKey="done" name={t('Done or partly done')} fill={colors.primary} radius={[6, 6, 0, 0]} maxBarSize={18} isAnimationActive={animate} animationDuration={duration} />
                <Bar dataKey="missed" name={t('Missed')} fill={`url(#${hatch})`} stroke={colors.inkSoft} strokeWidth={1.5} radius={[6, 6, 0, 0]} maxBarSize={18} isAnimationActive={animate} animationDuration={duration} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      {planned > 0 && (
        <>
          <ul className="planner-prog-legend">
            <li><Swatch kind="planned" /> {t('Planned (outline)')}</li>
            <li><Swatch kind="done" /> {t('Done or partly done (solid)')}</li>
            <li><Swatch kind="missed" /> {t('Missed (striped)')}</li>
          </ul>
          <Numbers>
            <table className="report-table">
              <thead>
                <tr>
                  <th scope="col">{t('Week starting')}</th>
                  <th scope="col" className="num">{t('Planned')}</th>
                  <th scope="col" className="num">{t('Done')}</th>
                  <th scope="col" className="num">{t('Partly done')}</th>
                  <th scope="col" className="num">{t('Missed')}</th>
                </tr>
              </thead>
              <tbody>
                {weeks.map((w) => (
                  <tr key={w.weekStart}>
                    <td>{d.short(w.weekStart)}</td>
                    <td className="num">{w.planned}</td>
                    <td className="num">{w.done}</td>
                    <td className="num">{w.partly}</td>
                    <td className="num">{w.missed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Numbers>
        </>
      )}
    </section>
  );
}

type Group = { key: string; unit: string; label: string | null; points: Point[] };

function groupsOf(s: TargetSeries): Group[] {
  const units = new Set(s.points.map((p) => p.unit));
  if (units.size <= 1) return [{ key: 'all', unit: s.points[0]?.unit ?? '', label: null, points: s.points }];
  const map = new Map<string, Group>();
  for (const p of s.points) {
    const k = `${p.activityId}|${p.unit}`;
    let g = map.get(k);
    if (!g) {
      g = { key: k, unit: p.unit, label: p.activity, points: [] };
      map.set(k, g);
    }
    g.points.push(p);
  }
  return [...map.values()];
}

const DASHES = [undefined, '6 4', '2 3', '8 3 2 3'];

function Mark({ shape, x, y }: { shape: number; x: number; y: number }) {
  const s = shape % 4;
  if (s === 0) return <circle cx={x} cy={y} r={4} className="planner-prog-mark" />;
  if (s === 1) return <rect x={x - 4} y={y - 4} width={8} height={8} className="planner-prog-mark" />;
  if (s === 2) return <polygon points={`${x},${y - 5} ${x + 5},${y + 4} ${x - 5},${y + 4}`} className="planner-prog-mark" />;
  return <polygon points={`${x},${y - 5} ${x + 5},${y} ${x},${y + 5} ${x - 5},${y}`} className="planner-prog-mark" />;
}

function ResultChart({ group, series, from, to, title }: { group: Group; series: TargetSeries; from: string; to: string; title: string }) {
  const t = useT();
  const d = useDates();
  const value = useValueText();
  const values = group.points.map((p) => p.value);
  const top = group.unit === '%' ? 110 : group.unit === '/5' ? 6 : headroom(Math.max(...values));
  const yTicks = group.unit === '%' ? [0, 25, 50, 75, 100] : group.unit === '/5' ? [0, 1, 2, 3, 4, 5] : [0, top / 2, top];
  const { colors, axis, tooltip, animate, duration } = useChartTheme();
  const activities = [...new Set(group.points.map((p) => p.activityId))];
  const names = new Map(group.points.map((p) => [p.activityId, p.activity]));
  const changes = series.changes.filter((c) => c.day >= from && c.day <= to);
  const review = series.reviewDate && series.reviewDate >= from && series.reviewDate <= to ? series.reviewDate : null;
  const achieved = series.achievedAt && series.achievedAt >= from && series.achievedAt <= to ? series.achievedAt : null;
  const first = group.points[0];
  const latest = group.points[group.points.length - 1];
  const byDay = new Map<number, Record<string, number>>();
  for (const p of group.points) {
    const k = ms(p.day);
    byDay.set(k, { ...(byDay.get(k) ?? { t: k }), [p.activityId]: p.value });
  }
  const rows = [...byDay.values()].sort((a, b) => a.t - b.t);

  return (
    <figure className="planner-prog-figure">
      {group.label && <figcaption className="planner-prog-sub">{group.label}</figcaption>}
      <div className="planner-prog-chart" role="img" style={{ width: '100%', height: 230 }}
        aria-label={t('Results for {target}: {count} recorded, first {first} on {firstDate}, latest {latest} on {latestDate}.', {
          target: title,
          count: group.points.length,
          first: value(first.value, first.unit),
          firstDate: d.short(first.day),
          latest: value(latest.value, latest.unit),
          latestDate: d.short(latest.day),
        })}
      >
        <ResponsiveContainer>
          <ComposedChart data={rows} margin={{ top: 30, right: 30, bottom: 0, left: 0 }}>
            <CartesianGrid stroke={colors.border} strokeDasharray="3 4" vertical={false} />
            <XAxis type="number" dataKey="t" domain={[ms(from), ms(to)]} ticks={[ms(from), ms(to)]} tickFormatter={(v: number) => d.short(isoDay(new Date(v)))} tick={axis} tickLine={false} axisLine={{ stroke: colors.border }} />
            <YAxis domain={[0, top]} ticks={yTicks} tickFormatter={(v: number) => value(Math.round(v * 10) / 10, group.unit)} tick={axis} tickLine={false} axisLine={false} width={56} />
            <Tooltip {...tooltip} labelFormatter={(v: number) => d.short(isoDay(new Date(v)))} formatter={(v: number, key: string) => [value(v, group.unit), names.get(key) ?? '']} />
            {changes.map((c, i) => (
              <ReferenceLine key={`c${i}`} x={ms(c.day)} stroke={colors.inkSoft} strokeDasharray="5 4" strokeWidth={1.5}
                label={{ value: String(i + 1), position: 'top', fill: colors.ink, fontSize: 12, fontWeight: 700, fontFamily: colors.font }} />
            ))}
            {review && (
              <ReferenceLine x={ms(review)} stroke={colors.ink} strokeDasharray="1 3" strokeWidth={1.5}
                label={{ value: t('Review'), position: 'insideBottom', fill: colors.ink, fontSize: 12, fontWeight: 700, fontFamily: colors.font }} />
            )}
            {achieved && (
              <ReferenceLine x={ms(achieved)} stroke={colors.success} strokeWidth={2}
                label={{ value: '✓', position: 'top', fill: colors.success, fontSize: 16, fontWeight: 700 }} />
            )}
            {activities.map((a, i) => (
              <Line key={a} type="monotone" dataKey={a} name={a} connectNulls stroke={colors.primary} strokeWidth={3} strokeDasharray={DASHES[i % DASHES.length]}
                dot={(p: { cx: number; cy: number; index: number; value: number | null }) => (p.value == null ? <g key={p.index} /> : <Mark key={p.index} shape={i} x={p.cx} y={p.cy} />)}
                activeDot={{ r: 7 }} isAnimationActive={animate} animationDuration={duration} />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {activities.length > 1 && (
        <ul className="planner-prog-legend">
          {activities.map((a, i) => (
            <li key={a}>
              <svg width="30" height="12" viewBox="0 0 30 12" aria-hidden="true" focusable="false">
                <line x1="1" x2="29" y1="6" y2="6" className="planner-prog-line" strokeDasharray={DASHES[i % DASHES.length]} />
                <Mark shape={i} x={15} y={6} />
              </svg>{' '}
              {names.get(a)}
            </li>
          ))}
        </ul>
      )}
      <p className="planner-prog-facts">
        {t('First: {value} on {date}', { value: value(first.value, first.unit), date: d.short(first.day) })}
        <br />
        {t('Latest: {value} on {date}', { value: value(latest.value, latest.unit), date: d.short(latest.day) })}
      </p>
      <Numbers>
        <table className="report-table">
          <thead>
            <tr>
              <th scope="col">{t('Date')}</th>
              <th scope="col">{t('Activity')}</th>
              <th scope="col" className="num">{t('Result')}</th>
            </tr>
          </thead>
          <tbody>
            {group.points.map((p, i) => (
              <tr key={i}>
                <td>{d.short(p.day)}</td>
                <td>{p.activity}</td>
                <td className="num">{value(p.value, p.unit)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Numbers>
    </figure>
  );
}

function TargetProgress({ series, from, to }: { series: TargetSeries; from: string; to: string }) {
  const t = useT();
  const d = useDates();
  const changes = series.changes.filter((c) => c.day >= from && c.day <= to);
  return (
    <article className="planner-prog-target">
      <h5>{series.statement}</h5>
      <p className="planner-prog-sub">
        {t('Goal: {goal}', { goal: series.goal })} · <span className={`planner-prog-status is-${series.status}`}>{t(TARGET_STATUS_LABEL[series.status])}</span>
      </p>
      <p className="planner-prog-facts">{t('{done} of {planned} sessions done', { done: series.done, planned: series.planned })}</p>
      {series.achievedAt && <p className="planner-prog-achieved">✓ {t('Achieved on {date}', { date: d.short(series.achievedAt) })}</p>}
      {series.points.length === 0 ? (
        <p className="muted">{t('No results recorded in this period.')}</p>
      ) : (
        groupsOf(series).map((g) => <ResultChart key={g.key} group={g} series={series} from={from} to={to} title={series.statement} />)
      )}
      {changes.length > 0 && (
        <div className="planner-prog-changes">
          <h6>{t('Changes in how it is taught')}</h6>
          <ol>
            {changes.map((c, i) => (
              <li key={i}>{t('{date}: {summary} ({activity})', { date: d.short(c.day), summary: c.summary, activity: c.activity })}</li>
            ))}
          </ol>
        </div>
      )}
      {series.reviewDate && <p className="planner-prog-facts">{t('Review date: {date}', { date: d.short(series.reviewDate) })}</p>}
    </article>
  );
}

export function TargetCharts({ targets, from, to }: { targets: TargetSeries[]; from: string; to: string }) {
  const t = useT();
  const groups = new Map<string, TargetSeries[]>();
  for (const s of targets) {
    const arr = groups.get(s.goal);
    if (arr) arr.push(s);
    else groups.set(s.goal, [s]);
  }
  return (
    <section className="planner-prog-section">
      <h3>{t('Each target')}</h3>
      {targets.length === 0 && <p className="muted">{t('There are no targets to show for this period.')}</p>}
      {[...groups.entries()].map(([goal, list]) => (
        <section key={goal} className="planner-prog-goal">
          <h4>{goal}</h4>
          <p className="planner-prog-sub">{t(AREA_LABEL[list[0].area])}</p>
          {list.map((s) => <TargetProgress key={s.targetId} series={s} from={from} to={to} />)}
        </section>
      ))}
    </section>
  );
}

export function AchievedSummary({ achieved, from, to }: { achieved: Analytics['achieved']; from: string; to: string }) {
  const t = useT();
  const d = useDates();
  const list = [...achieved].sort((a, b) => a.day.localeCompare(b.day));
  const max = Math.max(1, list.length);
  const top = headroom(max);
  const grad = useSvgId();
  const { colors, axis, tooltip, animate, duration } = useChartTheme();
  const data = [{ t: ms(from), n: 0 }, ...list.map((a, i) => ({ t: ms(a.day), n: i + 1 })), { t: ms(to), n: list.length }];
  return (
    <section className="planner-prog-section">
      <h3>{t('Targets achieved')}</h3>
      {list.length === 0 ? (
        <p className="muted">{t('No targets were achieved in this period.')}</p>
      ) : (
        <>
          <p className="planner-prog-lead">{list.length === 1 ? t('1 target achieved in this period') : t('{n} targets achieved in this period', { n: list.length })}</p>
          <div className="planner-prog-chart" role="img" aria-label={t('Targets achieved over time: {n} by {date}.', { n: list.length, date: d.short(to) })} style={{ width: '100%', height: 190 }}>
            <ResponsiveContainer>
              <ComposedChart data={data} margin={{ top: 16, right: 30, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`${grad}-a`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={colors.success} stopOpacity={0.25} />
                    <stop offset="100%" stopColor={colors.success} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={colors.border} strokeDasharray="3 4" vertical={false} />
                <XAxis type="number" dataKey="t" domain={[ms(from), ms(to)]} ticks={[ms(from), ms(to)]} tickFormatter={(v: number) => d.short(isoDay(new Date(v)))} tick={axis} tickLine={false} axisLine={{ stroke: colors.border }} />
                <YAxis domain={[0, top]} ticks={countTicks(top, max)} allowDecimals={false} tick={axis} tickLine={false} axisLine={false} width={32} />
                <Tooltip {...tooltip} labelFormatter={(v: number) => d.short(isoDay(new Date(v)))} formatter={(v: number) => [String(v), t('Targets achieved')]} />
                <Area type="stepAfter" dataKey="n" stroke="none" fill={`url(#${grad}-a)`} isAnimationActive={animate} animationDuration={duration} />
                <Line type="stepAfter" dataKey="n" stroke={colors.success} strokeWidth={3} dot={(p: { cx: number; cy: number; index: number }) => (p.index > 0 && p.index <= list.length ? <circle key={p.index} cx={p.cx} cy={p.cy} r={5} fill={colors.surface} stroke={colors.success} strokeWidth={3} /> : <g key={p.index} />)} activeDot={{ r: 7 }} isAnimationActive={animate} animationDuration={duration} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <ol className="planner-prog-achieved-list">
            {list.map((a) => (
              <li key={a.targetId}><span className="planner-prog-date">{d.short(a.day)}</span> {a.statement}</li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
}

export function PlanProgress({ learnerId, from, to }: { learnerId: string | null; from: string; to: string }) {
  const t = useT();
  const { data, loading, error } = usePlanAnalytics(learnerId, from, to);
  if (error) return <div className="error" role="alert">{error}</div>;
  if (!data) return loading ? <p className="muted">{t('Loading…')}</p> : null;
  return (
    <div className="planner-prog">
      <PracticeSummary weeks={data.weeks} />
      <TargetCharts targets={data.targets} from={from} to={to} />
    </div>
  );
}

export default function ProgressView() {
  const t = useT();
  const id = useId();
  const { learnerId } = useLearner();
  const [preset, setPreset] = useState<Preset>('weeks');
  const [range, setRange] = useState<Range>(() => presetRange('weeks'));
  const invalid = !range.from || !range.to || range.to < range.from;
  const { data, loading, error } = usePlanAnalytics(invalid ? null : learnerId, range.from, range.to);

  const choose = (p: Exclude<Preset, 'custom'>) => { setPreset(p); setRange(presetRange(p)); };
  const PRESETS: { id: Exclude<Preset, 'custom'>; label: string }[] = [
    { id: 'weeks', label: t('Last 6 weeks') },
    { id: 'term', label: t('This term') },
    { id: 'year', label: t('This year') },
  ];

  return (
    <div className="planner-prog">
      <div className="planner-prog-period">
        <div className="toolbar" role="group" aria-label={t('Period')}>
          {PRESETS.map((p) => (
            <button key={p.id} type="button" className="btn secondary" aria-pressed={preset === p.id} onClick={() => choose(p.id)}>{p.label}</button>
          ))}
        </div>
        <div className="row">
          <div className="field">
            <label htmlFor={`${id}-from`}>{t('From')}</label>
            <DateField id={`${id}-from`} value={range.from} onChange={(v) => { setPreset('custom'); setRange({ ...range, from: v ?? '' }); }} />
          </div>
          <div className="field">
            <label htmlFor={`${id}-to`}>{t('To')}</label>
            <DateField id={`${id}-to`} value={range.to} min={range.from} onChange={(v) => { setPreset('custom'); setRange({ ...range, to: v ?? '' }); }} />
          </div>
        </div>
      </div>
      {invalid && <div className="error" role="alert">{t('The end date must be on or after the start date.')}</div>}
      {error && <div className="error" role="alert">{error}</div>}
      {!invalid && !data && loading && <p className="muted">{t('Loading…')}</p>}
      {!invalid && data && (
        <>
          <PracticeSummary weeks={data.weeks} />
          <TargetCharts targets={data.targets} from={range.from} to={range.to} />
          <AchievedSummary achieved={data.achieved} from={range.from} to={range.to} />
        </>
      )}
    </div>
  );
}

